<template>
  <div class="modal fade" id="assignDisputeModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-account-plus me-2"></i>
            Assign Dispute - {{ disputeDetail.disputeId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Dispute Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="alert alert-info">
                <h6 class="alert-heading mb-2">
                  <i class="mdi mdi-information me-2"></i>
                  Dispute Summary
                </h6>
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Customer:</strong> {{ disputeDetail.customerName }}</p>
                    <p class="mb-1"><strong>Type:</strong> {{ formatDisputeType(disputeDetail.disputeType) }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Amount:</strong> {{ formatPrice(disputeDetail.disputeAmount) }}</p>
                    <p class="mb-0"><strong>Priority:</strong>
                      <span class="badge" :class="getPriorityClass(disputeDetail.priority)">{{ disputeDetail.priority }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Assignment Form -->
          <form @submit.prevent="assignDispute">
            <div class="row">
              <!-- Officer Selection -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Select Officer <span class="text-danger">*</span></label>
                  <select v-model="assignmentForm.selectedOfficer" class="form-select" required>
                    <option value="">Choose an officer...</option>
                    <option v-for="officer in availableOfficers" :key="officer.id" :value="officer">
                      {{ officer.name }} ({{ officer.department }})
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Department</label>
                  <input :value="assignmentForm.selectedOfficer?.department || ''"
                         type="text" class="form-control" readonly>
                </div>

                <div class="mb-3">
                  <label class="form-label">Priority Level <span class="text-danger">*</span></label>
                  <select v-model="assignmentForm.priority" class="form-select" required>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Expected Resolution Date</label>
                  <input v-model="assignmentForm.expectedDate" type="date" class="form-control"
                         :min="minDate">
                </div>
              </div>

              <!-- Officer Details & Workload -->
              <div class="col-md-6">
                <div v-if="assignmentForm.selectedOfficer" class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">Officer Details</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Name:</label>
                      <p class="fw-bold mb-2">{{ assignmentForm.selectedOfficer.name }}</p>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Department:</label>
                      <p class="mb-2">{{ assignmentForm.selectedOfficer.department }}</p>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Current Workload:</label>
                      <div class="progress mb-2">
                        <div class="progress-bar" :class="getWorkloadClass(getOfficerWorkload(assignmentForm.selectedOfficer.id))"
                             :style="{ width: getOfficerWorkload(assignmentForm.selectedOfficer.id) + '%' }">
                          {{ getOfficerWorkload(assignmentForm.selectedOfficer.id) }}%
                        </div>
                      </div>
                      <small class="text-muted">{{ getOfficerActiveDisputes(assignmentForm.selectedOfficer.id) }} active disputes</small>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Specialization:</label>
                      <div class="d-flex flex-wrap gap-1">
                        <span v-for="spec in getOfficerSpecialization(assignmentForm.selectedOfficer.department)"
                              :key="spec" class="badge bg-primary">{{ spec }}</span>
                      </div>
                    </div>

                    <div class="mb-0">
                      <label class="form-label">Average Resolution Time:</label>
                      <p class="mb-0 fw-bold text-success">{{ getOfficerAvgTime(assignmentForm.selectedOfficer.id) }} days</p>
                    </div>
                  </div>
                </div>

                <div v-else class="card bg-light">
                  <div class="card-body text-center">
                    <i class="mdi mdi-account-question mdi-48px text-muted mb-3"></i>
                    <p class="text-muted">Select an officer to view their details and workload</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Assignment Notes -->
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Assignment Notes</label>
                  <textarea v-model="assignmentForm.notes" class="form-control" rows="3"
                            placeholder="Add any specific instructions or notes for the assigned officer..."></textarea>
                </div>
              </div>
            </div>

            <!-- Notification Options -->
            <div class="row">
              <div class="col-12">
                <div class="card border">
                  <div class="card-header">
                    <h6 class="mb-0">Notification Settings</h6>
                  </div>
                  <div class="card-body">
                    <div class="form-check mb-2">
                      <input v-model="assignmentForm.notifications.email" class="form-check-input"
                             type="checkbox" id="notifyEmail">
                      <label class="form-check-label" for="notifyEmail">
                        Send email notification to assigned officer
                      </label>
                    </div>
                    <div class="form-check mb-2">
                      <input v-model="assignmentForm.notifications.sms" class="form-check-input"
                             type="checkbox" id="notifySMS">
                      <label class="form-check-label" for="notifySMS">
                        Send SMS notification to assigned officer
                      </label>
                    </div>
                    <div class="form-check mb-2">
                      <input v-model="assignmentForm.notifications.customer" class="form-check-input"
                             type="checkbox" id="notifyCustomer">
                      <label class="form-check-label" for="notifyCustomer">
                        Notify customer that dispute has been assigned
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="assignmentForm.notifications.dashboard" class="form-check-input"
                             type="checkbox" id="notifyDashboard" checked disabled>
                      <label class="form-check-label" for="notifyDashboard">
                        Add to officer's dashboard (automatically enabled)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Buttons -->
            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" :disabled="!canAssign || assigning" class="btn btn-primary">
                <span v-if="!assigning">
                  <i class="mdi mdi-account-check me-1"></i>
                  Assign Dispute
                </span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Assigning...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "AssignDisputeModal",
  props: {
    disputeDetail: {
      type: Object,
      default: () => ({})
    },
    availableOfficers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      assigning: false,
      assignmentForm: {
        selectedOfficer: null,
        priority: 'MEDIUM',
        expectedDate: '',
        notes: '',
        notifications: {
          email: true,
          sms: false,
          customer: true,
          dashboard: true
        }
      },

      // Mock workload data for officers
      officerWorkloads: {
        1: { activeDisputes: 8, workloadPercent: 60, avgResolutionDays: 3.2 },
        2: { activeDisputes: 12, workloadPercent: 85, avgResolutionDays: 2.8 },
        3: { activeDisputes: 5, workloadPercent: 35, avgResolutionDays: 4.1 },
        4: { activeDisputes: 15, workloadPercent: 95, avgResolutionDays: 5.2 },
        5: { activeDisputes: 7, workloadPercent: 50, avgResolutionDays: 3.8 }
      },

      // Officer specializations by department
      departmentSpecializations: {
        'Revenue': ['Billing Issues', 'Tax Disputes', 'Rate Calculations'],
        'Finance': ['Payment Processing', 'Refunds', 'Account Reconciliation'],
        'Customer Service': ['General Inquiries', 'Communication', 'Follow-ups'],
        'IT Support': ['System Issues', 'Technical Problems', 'Data Recovery'],
        'Legal': ['Legal Disputes', 'Compliance Issues', 'Regulatory Matters']
      }
    }
  },
  computed: {
    canAssign() {
      return this.assignmentForm.selectedOfficer && this.assignmentForm.priority;
    },

    minDate() {
      return moment().format('YYYY-MM-DD');
    }
  },
  mounted() {
    // Set default priority from dispute
    if (this.disputeDetail.priority) {
      this.assignmentForm.priority = this.disputeDetail.priority;
    }

    // Set default expected date (3 days from now for medium priority)
    const days = this.getDefaultDaysByPriority(this.disputeDetail.priority || 'MEDIUM');
    this.assignmentForm.expectedDate = moment().add(days, 'days').format('YYYY-MM-DD');
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDisputeType(type) {
      const types = {
        'BILLING_ERROR': 'Billing Error',
        'PAYMENT_NOT_REFLECTED': 'Payment Not Reflected',
        'OVERCHARGE': 'Overcharge',
        'DUPLICATE_CHARGE': 'Duplicate Charge',
        'REFUND_REQUEST': 'Refund Request',
        'OTHER': 'Other'
      };
      return types[type] || type;
    },

    getPriorityClass(priority) {
      switch (priority) {
        case 'LOW':
          return 'bg-light text-dark';
        case 'MEDIUM':
          return 'bg-info';
        case 'HIGH':
          return 'bg-warning';
        case 'URGENT':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getWorkloadClass(workloadPercent) {
      if (workloadPercent >= 90) return 'bg-danger';
      if (workloadPercent >= 70) return 'bg-warning';
      if (workloadPercent >= 50) return 'bg-info';
      return 'bg-success';
    },

    getOfficerWorkload(officerId) {
      return this.officerWorkloads[officerId]?.workloadPercent || 0;
    },

    getOfficerActiveDisputes(officerId) {
      return this.officerWorkloads[officerId]?.activeDisputes || 0;
    },

    getOfficerAvgTime(officerId) {
      return this.officerWorkloads[officerId]?.avgResolutionDays || 0;
    },

    getOfficerSpecialization(department) {
      return this.departmentSpecializations[department] || ['General'];
    },

    getDefaultDaysByPriority(priority) {
      switch (priority) {
        case 'URGENT':
          return 1;
        case 'HIGH':
          return 2;
        case 'MEDIUM':
          return 3;
        case 'LOW':
          return 5;
        default:
          return 3;
      }
    },

    async assignDispute() {
      try {
        this.assigning = true;

        // Validate assignment
        if (!this.validateAssignment()) {
          this.assigning = false;
          return;
        }

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit assignment event
        this.$emit('dispute-assigned', this.disputeDetail.disputeId, this.assignmentForm.selectedOfficer.name);

        // Show success message
        this.showAssignmentSuccess();

        // Close modal
        this.closeModal();

        this.assigning = false;
      } catch (error) {
        this.assigning = false;
        alert('Error assigning dispute: ' + error.message);
      }
    },

    validateAssignment() {
      if (!this.assignmentForm.selectedOfficer) {
        alert('Please select an officer to assign the dispute to');
        return false;
      }

      if (!this.assignmentForm.priority) {
        alert('Please select a priority level');
        return false;
      }

      // Check if officer is overloaded
      const workload = this.getOfficerWorkload(this.assignmentForm.selectedOfficer.id);
      if (workload >= 95) {
        const confirmOverload = confirm(
            `Warning: ${this.assignmentForm.selectedOfficer.name} has a very high workload (${workload}%). ` +
            'Are you sure you want to assign this dispute to them?'
        );
        if (!confirmOverload) {
          return false;
        }
      }

      return true;
    },

    showAssignmentSuccess() {
      // In a real application, this might trigger a toast notification
      const message = `Dispute ${this.disputeDetail.disputeId} has been successfully assigned to ${this.assignmentForm.selectedOfficer.name}`;

      // Show notifications that were selected
      const notifications = [];
      if (this.assignmentForm.notifications.email) notifications.push('Email sent');
      if (this.assignmentForm.notifications.sms) notifications.push('SMS sent');
      if (this.assignmentForm.notifications.customer) notifications.push('Customer notified');

      if (notifications.length > 0) {
        alert(message + '\n\nNotifications: ' + notifications.join(', '));
      } else {
        alert(message);
      }
    },

    closeModal() {
      const modalElement = document.getElementById('assignDisputeModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    resetForm() {
      this.assignmentForm = {
        selectedOfficer: null,
        priority: 'MEDIUM',
        expectedDate: moment().add(3, 'days').format('YYYY-MM-DD'),
        notes: '',
        notifications: {
          email: true,
          sms: false,
          customer: true,
          dashboard: true
        }
      };
    }
  },
  watch: {
    // Update expected date when priority changes
    'assignmentForm.priority'(newPriority) {
      const days = this.getDefaultDaysByPriority(newPriority);
      this.assignmentForm.expectedDate = moment().add(days, 'days').format('YYYY-MM-DD');
    },

    // Reset form when dispute changes
    disputeDetail: {
      handler() {
        if (this.disputeDetail.priority) {
          this.assignmentForm.priority = this.disputeDetail.priority;
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.progress {
  height: 8px;
}

.mdi-48px {
  font-size: 48px;
}

.badge {
  font-size: 0.75rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-label {
  font-size: 0.875rem;
}

.alert-heading {
  font-size: 1rem;
}
</style>